import _extends from "@babel/runtime/helpers/extends";
import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
var _dec, _dec2, _class, _class2, _descriptor, _descriptor2, _descriptor3, _class3;
/*
 *                     NOTICE
 *            COPYRIGHT 2023 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */
import { Expression } from "abreact/expression/Expression";
import { GuidedExpressionEditor } from "abreact/expression/ui/editor/GuidedExpressionEditor";
import { BaseGuidedExpressionEditor } from "abreact/expression/ui/editor/impl/BaseGuidedExpressionEditor";
import { IfThenElseEditor } from "abreact/expression/ui/editor/impl/model/impl/conditional/IfThenElseEditor";
import { IfThenElseModel, OperandName } from "abreact/expression/ui/editor/impl/model/impl/conditional/IfThenElseModel";
import { OperatorOperandValueImpl } from "abreact/expression/ui/editor/impl/model/impl/row/operandValue/OperatorOperandValueImpl";
import { ExpressionLoading } from "abreact/expression/ui/editor/impl/utils/ExpressionLoading";
import { ExpressionNotSpecified } from "abreact/expression/ui/editor/impl/utils/ExpressionNotSpecified";
import { GEECompletenessIndicator } from "abreact/expression/ui/editor/impl/utils/GEECompletenessIndicator";
import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { AbRadioButton } from "abreact/input/AbRadioButton";
import { AbHBox } from "abreact/layout/AbHBox";
import { AbVBox } from "abreact/layout/AbVBox";
import { AbSize } from "abreact/misc/AbSize";
import { AbButton, AbButtonType } from "absdk/commons/ui/misc/AbButton";
import { Assert } from "abweb/utils/Assert";
import { TypeUtils } from "abweb/utils/TypeUtils";
import { action, computed, makeObservable, observable } from "mobx";
import { observer } from "mobx-react";
import React from "react";
export let NonBooleanType = /*#__PURE__*/function (NonBooleanType) {
  NonBooleanType["CONDITIONAL_VALUE"] = "conditional-value";
  NonBooleanType["VALUE"] = "value";
  NonBooleanType["UNSET"] = "unset";
  return NonBooleanType;
}({});
export const ADDITIONAL_DATA_MARKING_TYPE_NON_CONDITIONAL = "non-boolean-value";
let GuidedNonBooleanExpressionEditor = (_dec = action.bound, _dec2 = action.bound, observer(_class = (_class2 = class GuidedNonBooleanExpressionEditor extends BaseGuidedExpressionEditor {
  constructor(props) {
    super(props);
    _initializerDefineProperty(this, "_operatorOperandValue", _descriptor, this);
    _initializerDefineProperty(this, "_nonBooleanType", _descriptor2, this);
    _initializerDefineProperty(this, "_complete", _descriptor3, this);
    _defineProperty(this, "_ifThenElseModel", void 0);
    this.applyOperatorOperandValue = this.applyOperatorOperandValue.bind(this);
    this._complete = false;
    makeObservable(this);
  }
  get complete() {
    return this._complete;
  }
  set complete(value) {
    this._complete = value;
  }
  setModel(model) {
    super.setModel(model);
    this.complete = model.isComplete();
    if (this.nonBooleanType == null) {
      this.initNonBooleanType(this.computeInitialNonBooleanType(), model.rowModel.operatorOperandValue);
    }
  }
  computeInitialNonBooleanType() {
    const initialOperatorID = this._model?.rowModel?.operatorOperandValue?.expOperator?.id;
    let initialNonBooleanType = NonBooleanType.UNSET;
    if (initialOperatorID != null) {
      if (this.isConditionalValueFunctionInDomain() && initialOperatorID === this.getConditionalValueFunctionExpOperator().id) {
        initialNonBooleanType = NonBooleanType.CONDITIONAL_VALUE;
      }
      if (this.isValueFunctionInDomain() && initialOperatorID === this.getValueFunctionExpOperator().id) {
        initialNonBooleanType = NonBooleanType.VALUE;
      }
    }
    return initialNonBooleanType;
  }
  get guidedExpressionEditor() {
    if (this.nonBooleanType == null) {
      return /*#__PURE__*/React.createElement(ExpressionLoading, null);
    }
    return /*#__PURE__*/React.createElement(AbVBox, {
      spacing: true,
      className: "guided-expression-editor ab-gee-nonboolean-editor"
    }, this.radios, this.editor, this.clearButton);
  }
  get nonBooleanType() {
    return this._nonBooleanType;
  }
  set nonBooleanType(value) {
    this._nonBooleanType = value;
  }
  get operatorOperandValue() {
    return this._operatorOperandValue ?? this._model.rowModel.operatorOperandValue;
  }
  get valueRadioLabel() {
    return this.props.propsExtension.nonBooleanConfiguration.valueLabel ?? this.getValueFunctionExpOperator().label;
  }
  get conditionalValueRadioLabel() {
    return this.props.propsExtension.nonBooleanConfiguration.conditionalValueLabel ?? this.getConditionalValueFunctionExpOperator().label;
  }
  get isUnset() {
    return this.nonBooleanType === NonBooleanType.UNSET;
  }
  get clearButton() {
    if (!this.isUnset) {
      return /*#__PURE__*/React.createElement(AbButton, {
        className: "ab-gee-nonboolean-editor-clear-button",
        dataTestId: "ab-gee-nonboolean-editor-clear-button",
        label: AbReactResources.nonboolean_editor_clear_button(),
        size: AbSize.EXTRA_SMALL,
        type: AbButtonType.DEFAULT,
        onClick: () => this.handleNonBooleanTypeChange(NonBooleanType.UNSET)
      });
    } else {
      return null;
    }
  }
  get radios() {
    const radioButtons = [];
    if (this.isValueFunctionInDomain()) {
      radioButtons.push(/*#__PURE__*/React.createElement(AbRadioButton, {
        className: "ab-gee-nonboolean-editor-radio",
        dataTestId: "ab-gee-nonboolean-editor-radio-value",
        label: this.valueRadioLabel,
        value: this.nonBooleanType === NonBooleanType.VALUE,
        groupName: "non-boolean-type",
        onChange: () => this.initNonBooleanType(NonBooleanType.VALUE)
      }));
    }
    if (this.isConditionalValueFunctionInDomain()) {
      radioButtons.push(/*#__PURE__*/React.createElement(AbRadioButton, {
        className: "ab-gee-nonboolean-editor-radio",
        dataTestId: "ab-gee-nonboolean-editor-radio-conditional-value",
        label: this.conditionalValueRadioLabel,
        value: this.nonBooleanType === NonBooleanType.CONDITIONAL_VALUE,
        groupName: "non-boolean-type",
        onChange: () => this.initNonBooleanType(NonBooleanType.CONDITIONAL_VALUE)
      }));
    }
    return /*#__PURE__*/React.createElement(AbHBox, {
      spacing: true,
      className: "ab-gee-nonboolean-editor-radios"
    }, radioButtons, this.nonBooleanType !== NonBooleanType.UNSET && /*#__PURE__*/React.createElement(GEECompletenessIndicator, {
      isComplete: this.complete,
      tooltip: AbReactResources.expression_incomplete_function_tooltip()
    }));
  }
  handleNonBooleanTypeChange(type) {
    const isChangeToUnset = type === NonBooleanType.UNSET;
    this.nonBooleanType = type;
    if (isChangeToUnset) {
      this.clearModel();
    }
  }
  get nonBooleanValueTypeID() {
    return this.props.propsExtension.nonBooleanConfiguration.outputValueTypeID;
  }
  get valueFunctionName() {
    return `${this.nonBooleanValueTypeID}_value`;
  }
  get conditionalValueFunctionName() {
    return `${this.nonBooleanValueTypeID}_if_then_else`;
  }
  isValueFunctionInDomain() {
    return this.props.expressionDomainModel.findExpOperator(this.valueFunctionName) != null;
  }
  isConditionalValueFunctionInDomain() {
    return this.props.expressionDomainModel.findExpOperator(this.conditionalValueFunctionName) != null;
  }
  getConditionalValueFunctionExpOperator() {
    return this.props.expressionDomainModel.getExpOperator(this.conditionalValueFunctionName);
  }
  getValueFunctionExpOperator() {
    return this.props.expressionDomainModel.getExpOperator(this.valueFunctionName);
  }
  get editor() {
    if (this.nonBooleanType === NonBooleanType.UNSET) {
      return /*#__PURE__*/React.createElement(ExpressionNotSpecified, null);
    } else if (this.nonBooleanType === NonBooleanType.VALUE) {
      return this.valueEditor;
    } else {
      return this.ifThenElseEditor;
    }
  }
  get ifThenElseEditor() {
    return /*#__PURE__*/React.createElement(IfThenElseEditor, {
      applyOperatorOperandValue: op => op && this.applyOperatorOperandValue(op),
      expressionDomainModel: this.getExpressionDomainModel(),
      ifThenElseModel: this.ifThenElseModel,
      operandName: GuidedNonBooleanExpressionEditor.OPERAND_NAME,
      expressionComponentFactory: this.props.componentFactory,
      generateId: this._model.generateId
    });
  }
  get ifThenElseModel() {
    if (this._ifThenElseModel == null) {
      this._ifThenElseModel = this.operatorOperandValue != null ? IfThenElseModel.create(this.operatorOperandValue, this.getExpressionDomainModel()) : IfThenElseModel.createEmpty(this.getCurrentExpOperator(),
      // use the same id if there is already an operatorOperandValue on the model
      // otherwise, generate a new one
      this._model.rowModel.id, this.getExpressionDomainModel());
    }
    return this._ifThenElseModel;
  }
  get valueEditor() {
    let editor = null;
    if (this.operatorOperandValue) {
      const {
        outputValueCategoryID,
        outputValueTypeID
      } = this.props.propsExtension.nonBooleanConfiguration;
      const baseProps = {
        componentFactory: this.props.componentFactory,
        expressionDomainModel: this.getExpressionDomainModel(),
        operatorOperandValue: this.operatorOperandValue,
        overrideValueCategoryID: outputValueCategoryID,
        overrideValueTypeID: outputValueTypeID,
        hideOperatorDropdown: true,
        operandValueEditorProps: this.operandValueEditorProps,
        expTemplateConfiguration: undefined
      };
      editor = this.props.componentFactory.getFunctionOperandValueEditor({
        isReadonly: false,
        ...baseProps,
        operandName: GuidedNonBooleanExpressionEditor.OPERAND_NAME,
        operatorOperandValue: this.operatorOperandValue,
        generateId: this._model.generateId,
        applyOperatorOperandValue: op => op && this.applyOperatorOperandValue(op)
      });
    }
    return /*#__PURE__*/React.createElement(AbHBox, {
      key: this.operatorOperandValue?.expOperator?.id,
      className: "ab-gee-nonboolean-editor-value-input",
      dataTestId: "ab-gee-nonboolean-editor-value-input"
    }, editor);
  }
  getExpressionDomainModel() {
    return this.props.expressionDomainModel;
  }
  get operandValueEditorProps() {
    const operatorOperandValue = this._model.rowModel.operatorOperandValue;
    const operandValue = operatorOperandValue?.argumentOperandValuesByOperandName.get("value");
    const baseProps = {
      operandName: GuidedNonBooleanExpressionEditor.OPERAND_NAME,
      operandValue: operandValue,
      expressionDomainModel: this.getExpressionDomainModel(),
      liveViewSliceModel: this.props.liveViewSliceModel,
      dataTestId: GuidedNonBooleanExpressionEditor.DATA_TEST_ID,
      generateId: this._model.generateId,
      operatorOperandValue: operatorOperandValue
    };
    if (this.props.isReadonly) {
      return {
        isReadonly: true,
        ...baseProps
      };
    } else {
      return {
        isReadonly: false,
        ...baseProps,
        customizationFunctions: this.props.customizationFunctions,
        applyOperandValue: TypeUtils.emptyFunction
      };
    }
  }
  getNonConditionalValue(operatorOperandValue) {
    return operatorOperandValue?.argumentOperandValuesByOperandName.get("value");
  }
  setupNonConditionalLabelIfNeeded(operatorOperandValue) {
    let resultingOperatorOperandValue = operatorOperandValue;
    if (this.getNonConditionalValue(resultingOperatorOperandValue) != null) {
      const value = this.getNonConditionalValue(resultingOperatorOperandValue);
      const expArgumentOperandForValue = this.props.expressionDomainModel.findExpArgumentOperandForExpOperator(resultingOperatorOperandValue.expOperator.id, "value");
      let valueLabel = OperatorOperandValueImpl.isInstance(value) ? this.props.propsExtension.nonBooleanConfiguration.compactSummaryValueLabelForFunction : this.props.propsExtension.nonBooleanConfiguration.compactSummaryValueLabel ?? expArgumentOperandForValue?.label;
      resultingOperatorOperandValue = resultingOperatorOperandValue.withExpAdditionalData({
        type: ADDITIONAL_DATA_MARKING_TYPE_NON_CONDITIONAL,
        values: {
          "valueLabel": valueLabel ?? ""
        }
      });
    }
    return resultingOperatorOperandValue;
  }
  applyOperatorOperandValue(operatorOperandValue) {
    Assert.assert(!this.props.isReadonly);
    let model = this._model;
    let {
      rowModel
    } = model;
    let newOperatorOperandValue;
    newOperatorOperandValue = operatorOperandValue.withOperandName(GuidedNonBooleanExpressionEditor.OPERAND_NAME).withId(rowModel.id);
    if (this.nonBooleanType === NonBooleanType.VALUE) {
      newOperatorOperandValue = this.setupNonConditionalLabelIfNeeded(newOperatorOperandValue);
    }
    this._operatorOperandValue = newOperatorOperandValue;
    if (!GuidedExpressionEditor.AbstractModel.AbstractRowModel.AbstractOperandValue.equals(operatorOperandValue, rowModel.operatorOperandValue)) {
      this.applyOperatorOperandValueToModel(newOperatorOperandValue);
    }
    return newOperatorOperandValue;
  }
  applyOperatorOperandValueToModel(operatorOperandValue) {
    let model = this._model;
    let {
      rowModel
    } = model;
    if (!GuidedExpressionEditor.AbstractModel.AbstractRowModel.AbstractOperandValue.equals(operatorOperandValue, rowModel.operatorOperandValue)) {
      model = model.withRowModel(rowModel.withOperatorOperandValue(operatorOperandValue));
      this.complete = model.isComplete();
      this.applyModel(model);
    }
  }
  getCurrentExpOperator() {
    if (this.nonBooleanType === NonBooleanType.VALUE) {
      return this.getValueFunctionExpOperator();
    } else {
      return this.getConditionalValueFunctionExpOperator();
    }
  }
  clearModel() {
    const expOperator = this.getCurrentExpOperator();
    const operatorOperandValue = new OperatorOperandValueImpl({
      id: this._model.rowModel.id,
      ...GuidedExpressionEditor.AbstractModel.AbstractRowModel.OperatorOperandValue.partialStateFromExpOperator(expOperator, this.props.expressionDomainModel)
    });
    let model = this._model.withRowModel(this._model.rowModel.withOperatorOperandValue(operatorOperandValue));
    this.applyModel(model);
  }
  initNonBooleanType(type, operatorOperandValue) {
    this.handleNonBooleanTypeChange(type);
    this.validateNonBooleanOperator();
    if (operatorOperandValue == null || operatorOperandValue.expOperator.id !== this.getCurrentExpOperator().id) {
      const expOperator = this.getCurrentExpOperator();
      operatorOperandValue = new OperatorOperandValueImpl({
        id: this._model.rowModel.id,
        ...GuidedExpressionEditor.AbstractModel.AbstractRowModel.OperatorOperandValue.partialStateFromExpOperator(expOperator, this.props.expressionDomainModel)
      });
    }
    if (operatorOperandValue && operatorOperandValue !== this._model.rowModel.operatorOperandValue) {
      this.applyOperatorOperandValue(operatorOperandValue);
    }
  }
  validateNonBooleanOperator() {
    const expOperator = this.getCurrentExpOperator();
    const messageForOperatorFunction = message => {
      return `${message} for operator ${expOperator.id}`;
    };
    switch (this.nonBooleanType) {
      case NonBooleanType.VALUE:
        const valueOperand = this.props.expressionDomainModel.findExpArgumentOperandForExpOperator(expOperator.id, "value");
        Assert.assert(valueOperand != null, messageForOperatorFunction(`"value" argument operand not found`));
        Assert.assert(!!valueOperand.optional === false, messageForOperatorFunction(`"${valueOperand.name}" must be required`));
        break;
      case NonBooleanType.CONDITIONAL_VALUE:
        const ifOperand = this.props.expressionDomainModel.findExpArgumentOperandForExpOperator(expOperator.id, OperandName.IF);
        const thenOperand = this.props.expressionDomainModel.findExpArgumentOperandForExpOperator(expOperator.id, OperandName.THEN);
        const elseOperand = this.props.expressionDomainModel.findExpArgumentOperandForExpOperator(expOperator.id, OperandName.ELSE);
        Assert.assert(ifOperand != null, messageForOperatorFunction(`"${OperandName.IF}" argument operand not found`));
        Assert.assert(ifOperand.categoryID === Expression.Domain.Constants.CATEGORY_SUB_EXPRESSION, messageForOperatorFunction(`"${OperandName.IF}" must have a category of ${Expression.Domain.Constants.CATEGORY_SUB_EXPRESSION}`));
        Assert.assert(!!ifOperand.optional === false, messageForOperatorFunction(`"${OperandName.IF}" must be required`));
        Assert.assert(thenOperand != null, messageForOperatorFunction(`"${OperandName.THEN}" argument operand not found`));
        Assert.assert(!!thenOperand.optional === false, messageForOperatorFunction(`"${OperandName.THEN}" must be required`));
        Assert.assert(elseOperand != null, messageForOperatorFunction(`"${OperandName.ELSE}" argument operand not found`));
        Assert.assert(!!elseOperand.optional, messageForOperatorFunction(`"${OperandName.ELSE}" must be not required`));
        break;
    }
  }
}, _descriptor = _applyDecoratedDescriptor(_class2.prototype, "_operatorOperandValue", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class2.prototype, "_nonBooleanType", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class2.prototype, "_complete", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class2.prototype, "guidedExpressionEditor", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "guidedExpressionEditor"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "handleNonBooleanTypeChange", [_dec], Object.getOwnPropertyDescriptor(_class2.prototype, "handleNonBooleanTypeChange"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "operandValueEditorProps", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "operandValueEditorProps"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "initNonBooleanType", [_dec2], Object.getOwnPropertyDescriptor(_class2.prototype, "initNonBooleanType"), _class2.prototype), _class2)) || _class);
(function (_GuidedNonBooleanExpressionEditor2) {
  const OPERAND_NAME = _GuidedNonBooleanExpressionEditor2.OPERAND_NAME = "simple-output";
  const DATA_TEST_ID = _GuidedNonBooleanExpressionEditor2.DATA_TEST_ID = "f1";
})(GuidedNonBooleanExpressionEditor || (GuidedNonBooleanExpressionEditor = {}));
let GuidedNonBooleanExpressionEditorWrapper = observer(_class3 = class GuidedNonBooleanExpressionEditorWrapper extends React.Component {
  render() {
    const baseProps = {
      className: this.props.className,
      expExpressionDefinition: this.props.expExpressionDefinition,
      modelMediator: this.props.modelMediator,
      componentFactory: this.props.componentFactory,
      expressionDomainModel: this.props.expressionDomainModel,
      liveViewSliceModel: this.props.liveViewSliceModel,
      debug: this.props.debug,
      propsExtension: {
        nonBooleanConfiguration: this.props.nonBooleanConfiguration
      }
    };
    if (this.props.isReadonly) {
      return /*#__PURE__*/React.createElement(GuidedNonBooleanExpressionEditor, _extends({}, baseProps, {
        isReadonly: true
      }));
    } else {
      return /*#__PURE__*/React.createElement(GuidedNonBooleanExpressionEditor, _extends({}, baseProps, {
        isReadonly: false,
        customizationFunctions: this.props.customizationFunctions,
        changeTracker: this.props.changeTracker,
        onChange: this.props.onChange
      }));
    }
  }
}) || _class3;
export { GuidedNonBooleanExpressionEditorWrapper as GuidedNonBooleanExpressionEditor };
//# sourceMappingURL=GuidedNonBooleanExpressionEditor.js.map
